import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  DeleteTransactionResponse,
  ExportTransactionsPayload,
  MappedChipProps,
  PaginatedTransactionFilterList,
  PaginatedTransactionList,
  TransactionFiltersPayload
} from '../models/transaction.model';
import { map, Observable } from 'rxjs';
import { TitleCasePipe } from '@angular/common';
import { UtilityService } from './utility.service';
import { StatusEnum } from '../enums/status.enum';
import { ComplianceRatingEnum } from '../enums/compliance-rating.enum';
import { ActiveTableFilters, TableParams } from '../../shared/components/table/models/page.model';

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  private readonly http = inject(HttpClient);
  private readonly utilityService = inject(UtilityService);
  private readonly titleCasePipe = inject(TitleCasePipe);

  getPaginatedTransactionList(payload: TableParams): Observable<PaginatedTransactionList> {
    const activeTableFilters: ActiveTableFilters = {
      sort: payload?.sort,
      filters: payload?.filters
    };

    const dynamicQuery = this.buildActiveFiltersQuery(activeTableFilters);
    return this.http
      .get<PaginatedTransactionList>(
        `${environment.AGTRACE_API}v1/transactions?page=${payload.page}&pageSize=${payload.pageSize}${dynamicQuery}`
      )
      .pipe(
        map((res) => {
          return {
            ...res,
            results: res.results.map((result) => {
              return {
                ...result,
                eudrCompliantProps: this.mapComplianceRatingChipProps(result.eudrCompliant),
                statusProps: this.mapStatusChipProps(this.titleCasePipe.transform(result.status)! as StatusEnum)
              };
            })
          };
        })
      );
  }

  exportTransactions(payload: ExportTransactionsPayload) {
    const transactionIdsQuery = this.utilityService.constructQueryParam<number>(
      'transactionIds',
      payload.transactionIds
    );

    const projectCode = this.buildProjectCodeQuery(payload?.projectCode);
    const projectCodeQuery = payload.transactionIds.length ? `&${projectCode}` : `?${projectCode}`;
    const activeFiltersQuery = payload?.activeTableFilters
      ? this.buildActiveFiltersQuery(payload.activeTableFilters)
      : '';
    const multiTierOnlyQuery = payload.multiTierOnly ? '&multiTierOnly=true' : '';
    const directPlantationOnlyQuery = payload.directPlantationOnly ? '&directPlantationOnly=true' : '';

    return this.http.get<Blob>(
      `${environment.AGTRACE_API}v1/transactions/export${transactionIdsQuery}${projectCodeQuery}${activeFiltersQuery}${multiTierOnlyQuery}${directPlantationOnlyQuery}`,
      {
        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }

  /**
   * Mode 2 export — the full upstream chain(s) for the selected final transactions (AGT-1549).
   * 1 id → a single TransactionChain-<n>.xlsx; N ids → a TransactionChains.zip. The BE sets the
   * filename + content-type, so the caller just saves the blob under the Content-Disposition name.
   */
  exportTransactionChains(transactionIds: number[]) {
    return this.http.post<Blob>(`${environment.AGTRACE_API}v1/transactions/export/chains`, transactionIds, {
      responseType: 'blob' as 'json',
      observe: 'response'
    });
  }

  deleteTransactions(transactionIds: number[]): Observable<DeleteTransactionResponse[]> {
    return this.http.delete<DeleteTransactionResponse[]>(`${environment.AGTRACE_API}v1/transactions`, {
      body: { transactionIds }
    });
  }

  exportQuotaUsage(payload: { projectCode: string[]; activeTableFilters?: ActiveTableFilters }) {
    const projectCodeQuery = this.buildProjectCodeQuery(payload.projectCode);

    let dateQuery = '';
    if (payload.activeTableFilters?.filters?.['transactionDate']) {
      const { startDate, endDate } = this.utilityService.getDestructuredDateRange(
        payload.activeTableFilters.filters['transactionDate']
      );
      if (startDate) {
        dateQuery += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        dateQuery += `&endDate=${encodeURIComponent(endDate)}`;
      }
    }

    return this.http.get<Blob>(
      `${environment.AGTRACE_API}v1/transactions/export-quota-usage?${projectCodeQuery}${dateQuery}`,
      {
        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }

  private mapComplianceRatingChipProps(eudrCompliant: boolean) {
    const props: MappedChipProps = { backgroundColor: '', fontColor: '', label: 't' };

    if (eudrCompliant) {
      props.backgroundColor = 'var(--p-green-100)';
      props.fontColor = 'var(--p-green-500)';
      props.label = ComplianceRatingEnum.EUDR_COMPLIANT;
    } else {
      props.backgroundColor = 'var(--p-red-100)';
      props.fontColor = 'var(--p-red-500)';
      props.label = ComplianceRatingEnum.EUDR_NON_COMPLIANT;
    }

    return props;
  }

  getPaginatedTransactionFilters(payload: TransactionFiltersPayload): Observable<PaginatedTransactionFilterList> {
    const projectCodeQuery = payload.projectCode?.map((code) => `projectCode=${code}`).join('&');
    const searchQuery = payload?.search ? `&search=${payload.search}` : '';
    const multiTierOnlyQuery = payload.multiTierOnly ? '&multiTierOnly=true' : '';
    const directPlantationOnlyQuery = payload.directPlantationOnly ? '&directPlantationOnly=true' : '';
    return this.http
      .get<PaginatedTransactionFilterList>(
        `${environment.AGTRACE_API}v1/transactions/filters?field=${payload.field}&page=${payload.page}&pageSize=${payload.pageSize}&${projectCodeQuery}${searchQuery}${multiTierOnlyQuery}${directPlantationOnlyQuery}`
      )
      .pipe(
        map((paginatedFilterList) => {
          const updatedResults = paginatedFilterList.results.map((item) => ({
            ...item,
            label: !item.label || item.label === 'null' ? '-' : item.label
          }));

          return {
            ...paginatedFilterList,
            results: updatedResults
          };
        })
      );
  }

  private buildProjectCodeQuery(projectCodes: string[]) {
    return new HttpParams({ fromObject: { projectCode: projectCodes ?? [''] } });
  }

  // TODO @bryantt: move to utility service - combine with the one in price service
  private buildActiveFiltersQuery(activeFilters: ActiveTableFilters): string {
    const filters = activeFilters.filters ?? {};
    const sortQuery = activeFilters.sort ? `sort=${encodeURIComponent(activeFilters.sort)}` : '';
    const searchQuery = filters['search'] ? `search=${encodeURIComponent(filters['search'])}` : '';
    const keysWithSpecialHandling = ['search', 'transactionDate', 'role'];

    const dynamicQueries: string[] = [];

    // Special handling for transactionDate
    if (filters['transactionDate']) {
      const { startDate, endDate } = this.utilityService.getDestructuredDateRange(filters['transactionDate']);
      if (startDate) {
        dynamicQueries.push(`startDate=${encodeURIComponent(startDate)}`);
      }
      if (endDate) {
        dynamicQueries.push(`endDate=${encodeURIComponent(endDate)}`);
      }
    }

    const roleFilter = filters['role'];
    if (Array.isArray(roleFilter)) {
      for (const group of roleFilter) {
        if (Array.isArray(group) && group.length > 0) {
          const joined = group.filter(Boolean).join('+');
          dynamicQueries.push(`role=${encodeURIComponent(joined)}`);
        } else {
          dynamicQueries.push(`role=null`);
        }
      }
    }

    // Dynamically handle the rest of filters, skip special keys
    for (const key of Object.keys(filters)) {
      const value = filters[key];

      if (value == null || keysWithSpecialHandling.includes(key)) {
        continue; // Skip keys that are already handled manually
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          dynamicQueries.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
        }
      } else {
        dynamicQueries.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }

    const queryParts = [sortQuery, searchQuery, ...dynamicQueries].filter(Boolean);
    return queryParts.length > 0 ? `&${queryParts.join('&')}` : '';
  }

  private mapStatusChipProps(status: StatusEnum) {
    const props: MappedChipProps = { backgroundColor: '', fontColor: '', label: '' };
    switch (status.toUpperCase()) {
      case StatusEnum.APPROVED:
        props.backgroundColor = 'var(--p-green-100)';
        props.fontColor = 'var(--p-green-800)';
        props.label = StatusEnum.APPROVED;
        break;
      case StatusEnum.REJECTED:
        props.backgroundColor = 'var(--p-red-100)';
        props.fontColor = 'var(--p-red-800)';
        props.label = StatusEnum.REJECTED;
        break;
      case StatusEnum.EXPIRED:
        props.backgroundColor = 'var(--p-ds-secondary-bg)';
        props.fontColor = 'var(--p-ds-secondary-text)';
        props.label = StatusEnum.EXPIRED;
        break;
      default:
        return;
    }

    return props;
  }
}
